import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { SortOrder, SortAs } from '@ocean/types/dist/sort'
import { SortUtils, CommonUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { ApiAction, MiddlewareApiAction, createApiAction } from 'api'
import { UserUtils } from 'utils'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getSimulatorTraining,
  getSimulatorTrainingSchedule,
  getListSimulatorTrainingScheduleSessions,
  getSimulatorTrainingSessionLink,
  getSpecialUsers,
  stopSimulatorTrainingSession,
  getListSimulatorTrainingMaintenancePeriods,
  getListInstructorLedEnrollments,
} from 'modules/simulatorTrainings/common/api/simulatorTrainingService'
import {
  SimulatorTrainingScheduleSession,
  ListSimulatorTrainingScheduleSessionsModel,
  SimulatorTrainingMaintenancePeriod,
  SimulatorTrainingSessionType,
} from 'modules/simulatorTrainings/common/types'
import {
  Workstation,
  ListSimulatorTrainingMaintenanceModel,
  SimulatorInstructorLedEnrolledPerson,
} from 'modules/simulatorTrainings/common/types/instructor'
import { ScheduleSessionsUtils, WorkstationUtils } from 'modules/simulatorTrainings/common/utils'
import {
  SimulatorTrainingInstructorState,
  ApiSimulatorTrainingResponse,
  ApiSimulatorTrainingScheduleResponse,
  ApiListSimulatorTrainingScheduleSessionsResponse,
  ApiSpecialUsersResponse,
  ApiListSimulatorTrainingMaintenancePeriodsResponse,
  ApiListInstructorLedEnrollmentsResponse,
  ApiGetSimulatorTrainingSessionLinkResponse,
  SimulatorTrainingSessionRequestParams,
  LoadTrainingDetailsDataParams,
} from './types'

const initialState: SimulatorTrainingInstructorState = {
  trainingDetailsData: {},
  scheduleDetailsData: {},
  listScheduleSessionsData: {},
  instructorWorkstations: [],
  studentWorkstations: [],
  instructors: {},
  stopSimulatorTrainingSessionRequest: {},
  stoppedSimulatorTrainingSessions: [],
  listMaintenancePeriodsData: {},
  listInstructorLedEnrollmentsData: {},
  simulatorTrainingSessionLinkData: {},
  simulatorTrainingVirtualClassroomSessionLink: null,
}

const simulatorTrainingInstructorSlice = createSlice({
  name: 'simulatorTrainingInstructor',
  initialState,
  reducers: {
    trainingDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSimulatorTrainingResponse>>
    ) {
      state.trainingDetailsData = action.payload
    },
    scheduleDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSimulatorTrainingScheduleResponse>>
    ) {
      state.scheduleDetailsData = action.payload
    },
    clearDetailsData(state) {
      state.trainingDetailsData = initialState.trainingDetailsData
      state.scheduleDetailsData = initialState.scheduleDetailsData
      state.listScheduleSessionsData = initialState.listScheduleSessionsData
    },
    listScheduleSessionsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListSimulatorTrainingScheduleSessionsResponse>>
    ) {
      state.listScheduleSessionsData = action.payload
    },
    specialUsersLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSpecialUsersResponse>>
    ) {
      state.instructors = action.payload
    },
    setInstructorWorkstations(state, action: PayloadAction<Workstation[]>) {
      state.instructorWorkstations = action.payload
    },
    setStudentWorkstations(state, action: PayloadAction<Workstation[]>) {
      state.studentWorkstations = action.payload
    },
    stopSimulatorTrainingSessionRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.stopSimulatorTrainingSessionRequest = action.payload
    },
    setStoppedSimulatorTrainingSessions(state, action: PayloadAction<string>) {
      const sessionId = action.payload
      !state.stoppedSimulatorTrainingSessions.includes(sessionId) &&
        state.stoppedSimulatorTrainingSessions.push(sessionId)
    },
    simulatorTrainingSessionLinkLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiGetSimulatorTrainingSessionLinkResponse>>
    ) {
      state.simulatorTrainingSessionLinkData = action.payload
    },
    setSimulatorTrainingVirtualClassroomSessionLink(
      state,
      action: PayloadAction<Nullable<string>>
    ) {
      state.simulatorTrainingVirtualClassroomSessionLink = action.payload
    },
    listMaintenancePeriodsLoaded(
      state,
      action: PayloadAction<
        ApiMiddlewarePayload<ApiListSimulatorTrainingMaintenancePeriodsResponse>
      >
    ) {
      state.listMaintenancePeriodsData = action.payload
    },
    listInstructorLedEnrollmentsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListInstructorLedEnrollmentsResponse>>
    ) {
      state.listInstructorLedEnrollmentsData = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  clearDetailsData,
  trainingDetailsLoaded,
  scheduleDetailsLoaded,
  listScheduleSessionsLoaded,
  specialUsersLoaded,
  setInstructorWorkstations,
  setStudentWorkstations,
  stopSimulatorTrainingSessionRequested,
  setStoppedSimulatorTrainingSessions,
  listMaintenancePeriodsLoaded,
  listInstructorLedEnrollmentsLoaded,
  simulatorTrainingSessionLinkLoaded,
  setSimulatorTrainingVirtualClassroomSessionLink,
} = simulatorTrainingInstructorSlice.actions
export {
  clearDetailsData,
  listInstructorLedEnrollmentsLoaded,
  setSimulatorTrainingVirtualClassroomSessionLink,
}
export default simulatorTrainingInstructorSlice.reducer

const generateWorkstations =
  (response: ApiMiddlewarePayload<ApiSimulatorTrainingResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result) {
      const { instructorWorkplaces, traineeWorkplaces } = result.details
      dispatch(
        setInstructorWorkstations(WorkstationUtils.generateWorkStations(instructorWorkplaces, 'I'))
      )
      dispatch(
        setStudentWorkstations(WorkstationUtils.generateWorkStations(traineeWorkplaces, 'S'))
      )
    }
    dispatch(trainingDetailsLoaded(response))
  }

export const loadTrainingData = (trainingId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getSimulatorTraining(trainingId),
    },
    payloadAction: trainingDetailsLoaded,
    nextAction: generateWorkstations,
  })
}

export const handleLoadTrainingDetailsData =
  (
    response: ApiMiddlewarePayload<ApiSimulatorTrainingScheduleResponse>,
    params: LoadTrainingDetailsDataParams
  ): AppThunk =>
  async (dispatch, getState) => {
    const { error, result } = response
    if (!error && result) {
      const { id } =
        getState().simulatorTrainingInstructor.trainingDetailsData.result?.details || {}
      const { trainingId } = result.details
      id !== trainingId && dispatch(loadTrainingData(trainingId) as MiddlewareApiAction)
    }
    const { payloadAction } = params
    dispatch(payloadAction(response))
  }

export const loadScheduleData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getSimulatorTrainingSchedule(scheduleId),
    },
    payloadAction: scheduleDetailsLoaded,
    nextAction: {
      action: handleLoadTrainingDetailsData,
      params: { payloadAction: scheduleDetailsLoaded },
    },
  })
}

export const getScheduleSessionGroups = createSelector(
  (sessions: SimulatorTrainingScheduleSession[] = []) => sessions,
  sessions => ScheduleSessionsUtils.groupSessions(sessions)
)

export const getScheduleSessionsDuration = createSelector(
  (sessions: SimulatorTrainingScheduleSession[] = []) => sessions,
  sessions => ScheduleSessionsUtils.getSessionsDuration(sessions)
)

export const loadListScheduleSessionsData = (
  params: ListSimulatorTrainingScheduleSessionsModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListSimulatorTrainingScheduleSessions(params),
    },
    payloadAction: listScheduleSessionsLoaded,
  })
}

export const reloadListScheduleSessionsData = (
  params: ListSimulatorTrainingScheduleSessionsModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListSimulatorTrainingScheduleSessions(params),
    },
    payloadAction: listScheduleSessionsLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ simulatorTrainingInstructor }) =>
      simulatorTrainingInstructor.listScheduleSessionsData.result,
  })
}

const handleLoadSimulatorTrainingSessionLink =
  (
    response: ApiMiddlewarePayload<ApiGetSimulatorTrainingSessionLinkResponse>,
    params: { type: SimulatorTrainingSessionType }
  ): AppThunk =>
  async dispatch => {
    const { type } = params
    if (!response.error && response.result) {
      const { link } = response.result.link
      if (type === SimulatorTrainingSessionType.SimulatorEnvironment) {
        CommonUtils.openNewTab(link)
      }
      if (type === SimulatorTrainingSessionType.VirtualClassroom) {
        dispatch(setSimulatorTrainingVirtualClassroomSessionLink(link))
      }
    }
    dispatch(simulatorTrainingSessionLinkLoaded(response))
  }

export const loadSimulatorTrainingSessionLinkData = (
  sessionId: Guid,
  type: SimulatorTrainingSessionType
): ApiAction => {
  return createApiAction({
    apiCalls: {
      link: () => getSimulatorTrainingSessionLink(sessionId),
    },
    payloadAction: simulatorTrainingSessionLinkLoaded,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleLoadSimulatorTrainingSessionLink,
      params: { type },
    },
  })
}

export const loadSpecialUsersData = (): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getSpecialUsers(),
    },
    payloadAction: specialUsersLoaded,
  })
}

const handleStopSimulatorTrainingSessionRequest =
  (response: ApiMiddlewarePayload, params: SimulatorTrainingSessionRequestParams): AppThunk =>
  async dispatch => {
    const { sessionId, payloadAction } = params
    dispatch(payloadAction(response))
    if (!response.error) dispatch(setStoppedSimulatorTrainingSessions(sessionId))
  }

export const stopSimulatorTrainingSessionRequest = (sessionId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => stopSimulatorTrainingSession(sessionId),
    },
    payloadAction: stopSimulatorTrainingSessionRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleStopSimulatorTrainingSessionRequest,
      params: { sessionId, payloadAction: stopSimulatorTrainingSessionRequested },
    },
  })
}

export const getNearestMaintenancePeriod = createSelector(
  (maintenancePeriods: SimulatorTrainingMaintenancePeriod[] = []) => maintenancePeriods,
  maintenancePeriods =>
    [...maintenancePeriods].sort(
      SortUtils.sortByMultipleKeys(['startDate'], SortOrder.Ascending, SortAs.Date)
    )[0]
)

export const loadListMaintenancePeriodsData = (
  params: ListSimulatorTrainingMaintenanceModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListSimulatorTrainingMaintenancePeriods(params),
    },
    payloadAction: listMaintenancePeriodsLoaded,
    shouldCallApi: ({ simulatorTrainingInstructor }) =>
      !simulatorTrainingInstructor.listMaintenancePeriodsData.isLoading,
  })
}

export const checkIsUserEnrolledPerson = createSelector(
  (enrollments: SimulatorInstructorLedEnrolledPerson[] = []) => enrollments,
  enrollments => {
    const { login } = UserUtils.getUserAccountInfo()
    return enrollments.some(({ accountName }) => accountName === login)
  }
)

export const loadListInstructorLedEnrollmentsData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListInstructorLedEnrollments(scheduleId),
    },
    payloadAction: listInstructorLedEnrollmentsLoaded,
  })
}
